/**
 * Utilidades compartidas entre componentes del flujo de "Solicitud de
 * actualización de datos" — labels humanizados, fallback automático
 * camelCase → "Camel case", y sets de fieldKeys que requieren
 * tratamiento especial (archivos, empresa-only).
 *
 * Importadas tanto por el componente cliente como por los admin para
 * que el usuario nunca vea identifiers crudos en la UI.
 */

/**
 * Diccionario manual de labels en castellano para los `fieldKey` que el
 * backend usa hoy. Si llega un fieldKey no contemplado aquí, el front
 * cae a `humanizeFieldKey()` automático — sigue siendo legible.
 */
export const FIELD_LABELS: Record<string, string> = {
  // Personal
  nombres: 'Nombres',
  apellidos: 'Apellidos',
  fechaNacimiento: 'Fecha de nacimiento',
  sexo: 'Sexo',
  nacionalidad: 'Nacionalidad',

  // Documento
  numeroId: 'Número de documento',
  tipoId: 'Tipo de documento',
  fechaExp: 'Fecha de vencimiento del documento',
  fotoIdfrontal: 'Foto frontal del documento',
  fotoIdPosterior: 'Foto reverso del documento',
  selfie: 'Selfie del rostro',

  // Residencia
  paisResidencia: 'País de residencia',
  ciudadResidencia: 'Ciudad de residencia',
  direccion: 'Dirección',

  // Contacto
  email: 'Correo electrónico',
  telefono: 'Teléfono',
  prefijoTelefono: 'Prefijo telefónico',

  // Empresa
  razonSocial: 'Razón social',
  rutEmpresa: 'RUT de la empresa',
  nombreComercial: 'Nombre comercial',
  documentoIdTributario: 'Documento de identificación tributaria',
  estatuto: 'Estatuto de la empresa',
};

/**
 * Opciones de prefijo telefónico para el select dinámico. Mismo dataset
 * que usa el onboarding (`src/app/auth/.../signup.constants.ts`) para
 * mantener paridad — la lista se duplica adrede para no acoplar el
 * módulo del dashboard con el de auth.
 */
export interface PhonePrefixOption {
  code: string;   // `+56`, `+58`, ... — valor persistido en BD
  name: string;   // Nombre visible en el dropdown
  flag: string;   // Nombre del archivo en `assets/flags/`
  iso: string;    // Código ISO (3 letras) — informativo
}

export const PHONE_PREFIX_OPTIONS: ReadonlyArray<PhonePrefixOption> = [
  { code: '+56', name: 'Chile', flag: 'chile.png', iso: 'CHL' },
  { code: '+58', name: 'Venezuela', flag: 'venezuela.png', iso: 'VEN' },
  { code: '+54', name: 'Argentina', flag: 'argentina.png', iso: 'ARG' },
  { code: '+591', name: 'Bolivia', flag: 'bolivia.png', iso: 'BOL' },
  { code: '+55', name: 'Brasil', flag: 'brasil.png', iso: 'BRA' },
  { code: '+57', name: 'Colombia', flag: 'colombia.png', iso: 'COL' },
  { code: '+593', name: 'Ecuador', flag: 'ecuador.png', iso: 'ECU' },
  { code: '+595', name: 'Paraguay', flag: 'paraguay.png', iso: 'PRY' },
  { code: '+51', name: 'Perú', flag: 'peru.png', iso: 'PER' },
  { code: '+598', name: 'Uruguay', flag: 'uruguay.png', iso: 'URY' },
  { code: '+1', name: 'Estados Unidos', flag: 'usa.png', iso: 'USA' },
];

/** Devuelve la opción del prefijo por su `code` (ej. `'+56'`). */
export function findPhonePrefix(code: string | null | undefined): PhonePrefixOption | null {
  if (!code) return null;
  return PHONE_PREFIX_OPTIONS.find((o) => o.code === code) ?? null;
}

/**
 * Campo `fieldKey` que representa el prefijo telefónico — el componente
 * de input mapea este key a un `<select>` con banderas aunque el
 * backend haya enviado `fieldType: 'text'` (compat con backend legacy
 * que todavía no expone el tipo `phonePrefix`).
 */
export const PHONE_PREFIX_FIELD_KEY = 'prefijoTelefono';

/** FieldKey del número telefónico local (sin prefijo). */
export const PHONE_FIELD_KEY = 'telefono';

/**
 * Fields que se solicitan siempre en par: pedir el teléfono implica
 * pedir también el prefijo (y viceversa). El operador nunca puede
 * marcar uno sin el otro.
 */
export const PHONE_PAIR_FIELD_KEYS: ReadonlySet<string> = new Set([
  PHONE_FIELD_KEY,
  PHONE_PREFIX_FIELD_KEY,
]);

/**
 * Devuelve el field "compañero" de un par (teléfono ↔ prefijo). Si el
 * fieldKey pasado no forma parte de un par, devuelve `null`.
 */
export function pairedFieldOf(fieldKey: string): string | null {
  if (fieldKey === PHONE_FIELD_KEY) return PHONE_PREFIX_FIELD_KEY;
  if (fieldKey === PHONE_PREFIX_FIELD_KEY) return PHONE_FIELD_KEY;
  return null;
}

/**
 * Sanitiza el valor del campo `telefono`: sólo dígitos, sin espacios,
 * sin guiones ni signos. El backend espera un string numérico plano
 * ("948608491") y el prefijo va aparte.
 */
export function sanitizePhoneNumber(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D+/g, '');
}

/**
 * Sanitiza el valor del prefijo: mantiene un único `+` inicial + dígitos.
 * El select del cliente ya emite `+56` limpio, pero por defensa cubrimos
 * casos como `" +56 "`, `"+ 56"`, `"56"`, etc.
 */
export function sanitizePhonePrefix(value: unknown): string {
  if (value === null || value === undefined) return '';
  const digits = String(value).replace(/\D+/g, '');
  return digits ? `+${digits}` : '';
}

/**
 * Sanitiza cualquier field de la solicitud según su `fieldKey`. Para
 * los que no requieren tratamiento especial hace un `trim` básico.
 */
export function sanitizeFieldValue(fieldKey: string, value: unknown): unknown {
  if (fieldKey === PHONE_FIELD_KEY) return sanitizePhoneNumber(value);
  if (fieldKey === PHONE_PREFIX_FIELD_KEY) return sanitizePhonePrefix(value);
  if (typeof value === 'string') return value.trim();
  return value;
}

/**
 * `fieldKey` que el front trata como fecha aunque el backend los tipe
 * como `text`. Cubre el caso "backend expone fieldType text pero es
 * semánticamente una fecha".
 */
const DATE_FIELD_KEYS = new Set<string>([
  'fechaNacimiento',
  'fechaExp',
  'fechaVencimientoDocumento',
]);

export function isDateFieldKey(fieldKey: string): boolean {
  return DATE_FIELD_KEYS.has(fieldKey);
}

/**
 * Normaliza cualquier representación de fecha al formato `YYYY-MM-DD`
 * que espera `<input type="date">` y que persiste el backend según
 * el contrato ("1984-03-09").
 *
 * Acepta:
 *   - `'1984-03-09'`                       (formato canónico)
 *   - `'1984-03-09T00:00:00.000Z'`         (ISO 8601)
 *   - `'1984-03-09T05:00:00-05:00'`        (ISO con offset)
 *   - `Date` object
 *
 * Devuelve `''` cuando no puede interpretar el valor — el `<input date>`
 * queda vacío en lugar de crashear.
 *
 * IMPORTANTE: cuando el input llega con hora, se extrae el date part
 * "crudo" via regex (no `new Date()`). Usar Date acá dispararía shift
 * de zona horaria — por ej. "1984-03-09T00:00:00.000Z" en UTC-3 pasa a
 * "1984-03-08" localmente. La regex evita ese bug.
 */
export function normalizeToDateInput(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '';
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (!s) return '';
  // Extrae el date part sin importar si viene con T + hora + zona.
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  // Último recurso: intento parseo Date (formatos exóticos como "9/3/1984").
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

/**
 * Formatea una fecha (en cualquier formato aceptado por
 * `normalizeToDateInput`) al formato local `DD/MM/YYYY` para mostrar al
 * usuario. Sin timezone shift porque trabajamos sobre el string
 * canónico ya normalizado.
 */
export function formatDateForDisplay(value: unknown): string {
  const norm = normalizeToDateInput(value);
  if (!norm) return '';
  const [y, m, d] = norm.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Ciclo de vida de un item en una solicitud:
 *   `PENDING → SUBMITTED → APPROVED / REJECTED`
 *
 * Orden de presentación en las listas (ambos, cliente y operador):
 *   1. `SUBMITTED`  — ya se envió, esperando revisión del operador.
 *   2. `PENDING`    — cliente aún debe completarlo.
 *   3. `REJECTED`   — rechazado por el operador, cliente en reintento.
 *   4. `APPROVED`   — cerrado con éxito.
 *
 * El operador ve arriba lo que requiere su atención (SUBMITTED); el
 * cliente ve arriba lo que ya envió + lo que aún debe hacer, dejando
 * los aprobados al final como cierre visual.
 */
const ITEM_STATUS_ORDER: Record<string, number> = {
  SUBMITTED: 0,
  PENDING: 1,
  REJECTED: 2,
  APPROVED: 3,
};

/**
 * Devuelve un nuevo array con los items ordenados por el ciclo de
 * vida. Estable dentro del mismo status (mantiene el orden original
 * del backend para items empatados — típicamente por `createdAt`).
 */
export function sortItemsByStatus<T extends { status?: string }>(
  items: ReadonlyArray<T>,
): T[] {
  const rankOf = (it: T): number => {
    const s = (it?.status ?? '').toString().toUpperCase();
    return ITEM_STATUS_ORDER[s] ?? 99;
  };
  return items
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const diff = rankOf(a.item) - rankOf(b.item);
      return diff !== 0 ? diff : a.idx - b.idx;
    })
    .map(({ item }) => item);
}

/** Fields que se manejan como archivo (upload S3) — display y validación distintos. */
export const FILE_FIELDS: ReadonlySet<string> = new Set([
  'fotoIdfrontal',
  'fotoIdPosterior',
  'selfie',
  'documentoIdTributario',
  'estatuto',
]);

/** Fields exclusivos de personas jurídicas (`tipoUsuarioId === 1`). */
export const COMPANY_FIELDS: ReadonlySet<string> = new Set([
  'razonSocial',
  'rutEmpresa',
  'nombreComercial',
  'documentoIdTributario',
  'estatuto',
]);

/**
 * Convierte un identifier técnico a un texto legible. Cubre
 * camelCase, snake_case y kebab-case. Es el fallback final cuando el
 * label no está en el diccionario manual.
 *
 * Ejemplos:
 *   "razonSocial"          → "Razón social" (si está en FIELD_LABELS)
 *   "ciudadDeResidencia"   → "Ciudad de residencia"
 *   "rut_empresa"          → "Rut empresa"
 *   "nombre-comercial"     → "Nombre comercial"
 */
export function humanizeFieldKey(fieldKey: string): string {
  if (!fieldKey) return '';
  if (FIELD_LABELS[fieldKey]) return FIELD_LABELS[fieldKey];

  const spaced = fieldKey
    // camelCase → camel Case
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // snake_case / kebab-case → con espacios
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();

  if (!spaced) return fieldKey;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Grupos visuales (mantienen orden de aparición en el form). */
export type FieldGroupKey = 'personal' | 'document' | 'selfie' | 'company' | 'other';

const PERSONAL_KEYS = new Set([
  'nombres',
  'apellidos',
  'fechaNacimiento',
  'sexo',
  'nacionalidad',
  'direccion',
  'ciudadResidencia',
  'paisResidencia',
  'email',
  'telefono',
]);

const DOCUMENT_KEYS = new Set([
  'numeroId',
  'tipoId',
  'fechaExp',
  'fotoIdfrontal',
  'fotoIdPosterior',
  'selfie',
]);

/** Decide a qué grupo visual pertenece un fieldKey. */
export function deriveGroupKey(fieldKey: string): FieldGroupKey {
  // El selfie va en su propia sección — antes caía bajo "Documento
  // de identidad" y confundía a los clientes ("¿selfie del documento?").
  if (fieldKey === 'selfie') return 'selfie';
  if (FILE_FIELDS.has(fieldKey) && DOCUMENT_KEYS.has(fieldKey)) return 'document';
  if (COMPANY_FIELDS.has(fieldKey)) return 'company';
  if (DOCUMENT_KEYS.has(fieldKey)) return 'document';
  if (PERSONAL_KEYS.has(fieldKey)) return 'personal';
  return 'other';
}

/**
 * Parsea un string como `usuarioId` (entero positivo). Retorna `null`
 * si no es un número puro — la búsqueda en ese caso cae al filtro `q`.
 *
 * Se aceptan tanto `'12'` como `'  12 '` y se rechazan `'12a'`, `'-5'`,
 * `'12.5'`, etc. — el ID es un entero positivo.
 */
export function parseUsuarioId(input: string): number | null {
  const trimmed = (input || '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Campos del usuario que el backend devuelve como **key de S3** (path
 * relativo dentro del bucket, ej. `CL/abc123/fotoIdfrontal.jpg`). Para
 * previsualizar el archivo el front tiene que firmarlas con
 * `S3UrlService.getReadUrl()` — es el mismo patrón que usa
 * `compliance-user.component` para mostrar los documentos del cliente.
 *
 * Cuando backend exponga `currentDocumentUrls` ya firmadas en el shape
 * de `UserForRequest`, este helper se vuelve un fallback.
 */
const DOCUMENT_KEY_FIELDS: ReadonlyArray<string> = [
  'fotoIdfrontal',
  'fotoIdPosterior',
  'selfie',
  'estatuto',
  'documentoIdTributario',
];

/**
 * Mínimo contrato de un firmador S3 — evita importar el service
 * concreto desde el util compartido (los utils no deben acoplarse a
 * Angular DI). Los componentes pasan su instancia inyectada.
 */
export interface S3KeySigner {
  getReadUrl(key: string | null | undefined): Promise<string | null>;
}

/**
 * Hidrata `user.currentDocumentUrls` firmando con `S3UrlService` las
 * keys que el backend devuelve en campos directos (`fotoIdfrontal`,
 * `fotoIdPosterior`, `selfie`, etc.). Mutación in-place: el caller
 * sigue usando la misma referencia del user.
 *
 * Respeta lo que ya esté en `currentDocumentUrls` — si backend en
 * algún momento empieza a devolver URLs firmadas, no las pisamos.
 */
export async function hydrateCurrentDocumentUrls(
  user: { currentDocumentUrls?: Record<string, string | null | undefined> } & Record<string, any>,
  signer: S3KeySigner,
): Promise<void> {
  if (!user) return;
  const urls: Record<string, string | null | undefined> = {
    ...(user.currentDocumentUrls || {}),
  };

  const pending: Array<Promise<void>> = [];
  for (const field of DOCUMENT_KEY_FIELDS) {
    // Si ya tenemos URL (firmada por backend), no la pisamos.
    if (urls[field]) continue;
    const key = user[field];
    if (typeof key !== 'string' || !key.trim()) continue;
    pending.push(
      signer
        .getReadUrl(key)
        .then((url) => {
          if (url) urls[field] = url;
        })
        .catch(() => {
          // Fail-silent: la celda queda con placeholder.
        }),
    );
  }

  await Promise.all(pending);
  user.currentDocumentUrls = urls;
}
